/**
 * EWAS Data Source Service
 * Higher-level service for working with EWAS data in CLEAR components
 */

import { ewasClient } from './ewasClient';
import type {
  EWASDataSource,
  EWASAlert,
  EWASMapLayer,
  EWASMapPoint,
  EWASStatistics,
  EWASQueryFilters,
  EWASSourceId,
  AnyEWASRecord,
  ACLEDEvent,
  IOMDTMRecord,
  FEWSNETRecord,
  DataminrAlert,
} from './types';

// Color mappings for sources
const SOURCE_COLORS: Record<EWASSourceId, string> = {
  acled: '#991B1B', // Dark red for conflict
  acled_cast: '#B91C1C', // Red for forecasted conflict
  iom_dtm: '#9333EA', // Purple for displacement
  idmc_gidd: '#7C3AED', // Violet for displacement
  idmc_idu: '#6D28D9', // Deep violet for displacement
  fewsnet: '#EA580C', // Orange for food insecurity
  glofas: '#3B82F6', // Blue for floods
  dataminr: '#F59E0B', // Amber for social alerts
  dtm_flash: '#A855F7', // Light purple for flash displacement
  reliefweb: '#6B7280', // Gray for reports
};

// Source name mappings
const SOURCE_NAMES: Record<EWASSourceId, string> = {
  acled: 'ACLED',
  acled_cast: 'ACLED CAST',
  iom_dtm: 'IOM DTM',
  idmc_gidd: 'IDMC-GIDD',
  idmc_idu: 'IDMC-IDU',
  fewsnet: 'FEWSNET',
  glofas: 'GloFAS',
  dataminr: 'Dataminr',
  dtm_flash: 'DTM Flash',
  reliefweb: 'ReliefWeb',
};

class DataSourceService {
  /**
   * Get all data sources with their current status
   */
  async getDataSources(): Promise<EWASDataSource[]> {
    const response = await ewasClient.getSources();
    return response.sources;
  }

  /**
   * Get pipeline statistics
   */
  async getStatistics(): Promise<EWASStatistics> {
    const response = await ewasClient.getStatistics();
    return response.statistics;
  }

  /**
   * Convert EWAS records to CLEAR Alert format
   */
  async getAlerts(filters?: EWASQueryFilters): Promise<EWASAlert[]> {
    const response = await ewasClient.getData(filters);
    return response.data.map(record => this.recordToAlert(record));
  }

  /**
   * Get alerts grouped by source
   */
  async getAlertsBySource(): Promise<Record<EWASSourceId, EWASAlert[]>> {
    const alerts = await this.getAlerts();
    const grouped: Partial<Record<EWASSourceId, EWASAlert[]>> = {};

    alerts.forEach(alert => {
      if (!grouped[alert.source]) {
        grouped[alert.source] = [];
      }
      grouped[alert.source]!.push(alert);
    });

    return grouped as Record<EWASSourceId, EWASAlert[]>;
  }

  /**
   * Get map layers for all EWAS data
   */
  async getMapLayers(filters?: EWASQueryFilters): Promise<EWASMapLayer[]> {
    const [acled, iom, fewsnet] = await Promise.all([
      ewasClient.getACLEDEvents(filters),
      ewasClient.getIOMDisplacement(filters),
      ewasClient.getFEWSNETData(filters),
    ]);

    const layers: EWASMapLayer[] = [
      {
        id: 'ewas-acled',
        sourceId: 'acled',
        name: 'ACLED Conflict Events',
        description: 'Armed conflict data from ACLED',
        color: SOURCE_COLORS.acled,
        iconType: 'marker',
        visible: true,
        dataPoints: acled.data.map(r => this.recordToMapPoint(r)),
      },
      {
        id: 'ewas-displacement',
        sourceId: 'iom_dtm',
        name: 'IOM Displacement',
        description: 'Displacement tracking from IOM DTM',
        color: SOURCE_COLORS.iom_dtm,
        iconType: 'marker',
        visible: true,
        dataPoints: iom.data.map(r => this.recordToMapPoint(r)),
      },
      {
        id: 'ewas-food',
        sourceId: 'fewsnet',
        name: 'FEWSNET Food Security',
        description: 'Food security classification from FEWSNET',
        color: SOURCE_COLORS.fewsnet,
        iconType: 'polygon',
        visible: true,
        dataPoints: fewsnet.data.map(r => this.recordToMapPoint(r)),
      },
    ];

    return layers;
  }

  /**
   * Get source color
   */
  getSourceColor(sourceId: EWASSourceId): string {
    return SOURCE_COLORS[sourceId] || '#6B7280';
  }

  /**
   * Get source display name
   */
  getSourceName(sourceId: EWASSourceId): string {
    return SOURCE_NAMES[sourceId] || sourceId;
  }

  /**
   * Convert any EWAS record to CLEAR Alert format
   */
  private recordToAlert(record: AnyEWASRecord): EWASAlert {
    const baseAlert: EWASAlert = {
      id: record.id,
      source: record.source,
      sourceName: SOURCE_NAMES[record.source] || record.source,
      title: this.generateAlertTitle(record),
      severity: record.severity,
      region: record.location.admin1 || record.location.name,
      locations: record.location.name,
      date: record.date,
      timestamp: record.timestamp,
      description: record.description,
      value: record.value,
      unit: record.unit,
      coordinates: [record.location.lng, record.location.lat],
      acknowledged: false,
      metadata: record.metadata,
    };

    // Add source-specific fields
    if (this.isACLEDEvent(record)) {
      baseAlert.window = '24h';
      baseAlert.confidence = record.isForecast ? '75%' : '92%';
      baseAlert.metadata = {
        ...baseAlert.metadata,
        fatalities: record.fatalities,
        eventType: record.eventType,
        actors: [record.actor1, record.actor2].filter(Boolean),
      };
    } else if (this.isIOMDTMRecord(record)) {
      baseAlert.window = '48h';
      baseAlert.confidence = '88%';
      baseAlert.metadata = {
        ...baseAlert.metadata,
        individuals: record.individuals,
        households: record.households,
        displacementType: record.displacementType,
      };
    } else if (this.isFEWSNETRecord(record)) {
      baseAlert.window = record.projectionPeriod || '30d';
      baseAlert.confidence = '85%';
      baseAlert.metadata = {
        ...baseAlert.metadata,
        ipcPhase: record.ipcPhase,
        populationInPhase: record.populationInPhase,
        analysisType: record.analysisType,
      };
    } else if (this.isDataminrAlert(record)) {
      baseAlert.window = '2h';
      baseAlert.confidence = `${Math.round(record.confidence * 100)}%`;
      baseAlert.metadata = {
        ...baseAlert.metadata,
        urgency: record.urgency,
        alertType: record.alertType,
      };
    }

    return baseAlert;
  }

  /**
   * Convert record to map point
   */
  private recordToMapPoint(record: AnyEWASRecord): EWASMapPoint {
    return {
      id: record.id,
      source: record.source,
      coordinates: [record.location.lng, record.location.lat],
      severity: record.severity,
      label: record.location.name,
      value: record.value,
      unit: record.unit,
      popup: {
        title: this.generateAlertTitle(record),
        details: this.getPopupDetails(record),
      },
    };
  }

  /**
   * Generate alert title based on record type
   */
  private generateAlertTitle(record: AnyEWASRecord): string {
    if (this.isACLEDEvent(record)) {
      const eventLabel = record.eventType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return `${eventLabel} - ${record.location.name}`;
    }
    if (this.isIOMDTMRecord(record)) {
      return `${record.displacementType.toUpperCase()} Movement - ${record.location.name}`;
    }
    if (this.isFEWSNETRecord(record)) {
      return `IPC Phase ${record.ipcPhase} - ${record.location.name}`;
    }
    if (this.isDataminrAlert(record)) {
      return `${record.alertType} Alert - ${record.location.name}`;
    }
    return `${record.variable} - ${record.location.name}`;
  }

  /**
   * Get popup details for map
   */
  private getPopupDetails(record: AnyEWASRecord): Record<string, string | number> {
    const details: Record<string, string | number> = {
      Source: SOURCE_NAMES[record.source] || record.source,
      Date: record.date,
      Severity: record.severity,
    };

    if (this.isACLEDEvent(record)) {
      details['Event Type'] = record.eventType;
      details['Fatalities'] = record.fatalities;
      if (record.actor1) details['Actor 1'] = record.actor1;
    } else if (this.isIOMDTMRecord(record)) {
      details['Individuals'] = record.individuals.toLocaleString();
      details['Households'] = record.households.toLocaleString();
      details['Type'] = record.displacementType;
    } else if (this.isFEWSNETRecord(record)) {
      details['IPC Phase'] = record.ipcPhase;
      details['Population'] = record.populationInPhase.toLocaleString();
    }

    return details;
  }

  // Type guards
  private isACLEDEvent(record: AnyEWASRecord): record is ACLEDEvent {
    return record.source === 'acled' || record.source === 'acled_cast';
  }

  private isIOMDTMRecord(record: AnyEWASRecord): record is IOMDTMRecord {
    return record.source === 'iom_dtm' || record.source === 'dtm_flash';
  }

  private isFEWSNETRecord(record: AnyEWASRecord): record is FEWSNETRecord {
    return record.source === 'fewsnet';
  }

  private isDataminrAlert(record: AnyEWASRecord): record is DataminrAlert {
    return record.source === 'dataminr';
  }
}

// Export singleton instance
export const dataSourceService = new DataSourceService();

// Export class for testing
export { DataSourceService };
